/**
 * Wird nur gemountet, wenn `state.status === "authenticated"` (__root.tsx) — keine Hooks/Effects für Gäste.
 * Pageviews + sync nach Einwilligung (localStorage).
 */
import { useEffect, useRef } from "react";
import { useLocation, useRouterState } from "@tanstack/react-router";
import { getStoredAnalyticsConsent } from "@/lib/analytics-consent";
import { pageViewPath } from "@/lib/page-view-path";
import { capturePageView, initPostHog, syncPostHogWithRoute } from "@/lib/posthog";

/**
 * The one seam, and why it exists.
 *
 * `capturePageView` returns on its FIRST line unless `getPostHogKey()` finds
 * `VITE_POSTHOG_KEY`, and `viteEnv()` is `import.meta.env ?? {}` — empty under Node by
 * construction. So a page view computed correctly here and then dropped on the way out
 * looked identical to a correct one from every test's point of view: the render test could
 * prove this component mounts and computes a path, never that the path ARRIVES.
 *
 * Injecting the sink is the same shape phase 12 gave the Stripe calls — an optional
 * parameter with the real function as its default, so no production call site changes and
 * no test-only export exists. The alternative was `mock.module()`, which needs Node 22.3+
 * while CI pins 20.
 */
export interface PostHogPageViewProps {
  /** Defaults to the real `capturePageView`; a test passes a spy. */
  onPageView?: (path: string, routeId?: string) => void;
}

export function PostHogPageView({ onPageView = capturePageView }: PostHogPageViewProps) {
  const location = useLocation();
  const routeId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.routeId as string | undefined,
  });
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Why searchStr and not search: see lib/page-view-path.ts, which owns and pins that trap.
    const path = pageViewPath(location);
    const pathname = location.pathname;
    if (prevPathRef.current === path) return;
    prevPathRef.current = path;

    /** Kurzer Delay nach Navigation: nicht im selben Tick wie TanStack/React-Commit. */
    const tid = window.setTimeout(() => {
      try {
        syncPostHogWithRoute(pathname);
        if (getStoredAnalyticsConsent() === "accepted") {
          initPostHog();
        }
        onPageView(path, routeId);
      } catch {
        /* Analytics darf Routing nicht killen */
      }
    }, 120);

    return () => window.clearTimeout(tid);
    /**
     * `onPageView` is deliberately NOT a dependency. It is a sink, stable by contract — the
     * default is a module import and a test passes one spy for the mount's lifetime. Adding
     * it cost a page view when I tried: an inline sink changes identity on every render, the
     * effect re-runs, its cleanup clears the pending 120 ms timer, and the re-run then exits
     * at `prevPathRef.current === path` without scheduling a replacement. The navigation is
     * silently never reported. Caught by the test below, on this component's own change.
     */
  }, [location.pathname, location.searchStr, routeId]);

  return null;
}
